<script setup lang="ts">
import { ref, computed } from 'vue'
import draggable from 'vuedraggable'
import { useOpsStore } from '@/stores/ops'
import { useObjectivesStore } from '@/stores/objectives'
import { useIAPStore } from '@/stores/iap'
import { useSyncStore } from '@/stores/sync'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosButton from '@/components/ui/FosButton.vue'

const props = defineProps<{ incidentId: string }>()
defineEmits<{ prev: []; next: [] }>()

const opsStore = useOpsStore()
const objectivesStore = useObjectivesStore()
const iapStore = useIAPStore()
const syncStore = useSyncStore()

const opId = computed(() => opsStore.selectedOpId ?? '')
const op = computed(() => opsStore.selectedOp)

// Objectives
const newObjectiveText = ref('')
const editingId = ref<string | null>(null)
const editingText = ref('')

const draggableObjectives = computed({
  get: () => [...objectivesStore.objectives],
  set: (ordered) => {
    if (!opId.value) return
    objectivesStore.reorderObjectives(props.incidentId, opId.value, ordered)
  },
})

async function addObjective() {
  const text = newObjectiveText.value.trim()
  if (!text || !opId.value) return
  await objectivesStore.addObjective(props.incidentId, opId.value, text)
  newObjectiveText.value = ''
}

function startEdit(id: string, text: string) {
  editingId.value = id
  editingText.value = text
}

async function commitEdit(id: string) {
  if (!opId.value) return
  const text = editingText.value.trim()
  if (text) await objectivesStore.updateObjective(props.incidentId, opId.value, id, text)
  editingId.value = null
}

async function removeObjective(id: string) {
  if (!opId.value) return
  await objectivesStore.removeObjective(props.incidentId, opId.value, id)
}

// OP text fields — save on blur
async function saveOpField(field: string, value: string | boolean) {
  if (!opId.value) return
  syncStore.pendingWrites++
  try {
    await opsStore.updateOp(props.incidentId, opId.value, { [field]: value } as never)
  } finally {
    syncStore.pendingWrites--
  }
}

// Prepared by
const preparedBy202 = computed(() => iapStore.iap?.prepared_by_202 ?? '')
async function savePreparedBy(value: string) {
  if (!opId.value) return
  await iapStore.savePreparedBy(props.incidentId, opId.value, '202', value)
}
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 202 · Required</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Incident Objectives</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Operational objectives, safety message, weather forecast, and timing for this period.</p>
    </div>

    <!-- Objectives group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Operational Period Objectives</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <draggable
          v-model="draggableObjectives"
          item-key="objective_id"
          handle=".drag-handle"
          class="flex flex-col gap-1"
        >
          <template #item="{ element, index }">
            <div class="grid items-center gap-2.5 px-2.5 py-2 border border-transparent rounded hover:border-border/60 hover:bg-surface-container-low group" style="grid-template-columns: 22px 22px 1fr auto;">
              <span class="drag-handle material-symbols-outlined text-[18px] text-outline cursor-grab inline-flex items-center justify-center" style="font-variation-settings: 'wght' 400;">drag_indicator</span>
              <span class="font-mono font-semibold text-[12px] text-outline text-center">{{ index + 1 }}</span>
              <template v-if="editingId === element.objective_id">
                <FosInput
                  v-model="editingText"
                  @blur="commitEdit(element.objective_id)"
                  @keyup.enter="commitEdit(element.objective_id)"
                  @keyup.escape="editingId = null"
                />
              </template>
              <p
                v-else
                class="font-body text-[13px] text-on-surface cursor-text bg-transparent border border-transparent rounded px-2 py-1.5 hover:border-border/60 hover:bg-white m-0"
                @click="startEdit(element.objective_id, element.text)"
              >{{ element.text }}</p>
              <button
                class="w-6 h-6 inline-flex items-center justify-center text-outline hover:text-error border border-transparent rounded hover:border-border hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                @click="removeObjective(element.objective_id)"
              >
                <span class="material-symbols-outlined text-[16px]">close</span>
              </button>
            </div>
          </template>
        </draggable>

        <div class="flex gap-2 mt-1">
          <FosInput v-model="newObjectiveText" placeholder="New objective…" @keyup.enter="addObjective" />
          <FosButton variant="secondary" size="sm" @click="addObjective">Add</FosButton>
        </div>
      </div>
    </div>

    <!-- Safety + Weather (2-column) -->
    <div class="grid gap-3" style="grid-template-columns: 1fr 1fr;">
      <!-- Safety Message -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">General Safety Message</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="op?.safety_message ?? ''"
            placeholder="Safety message for this operational period…"
            :rows="4"
            @blur="saveOpField('safety_message', ($event.target as HTMLTextAreaElement).value)"
          />
        </div>
      </div>
      <!-- Weather Forecast -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Weather Forecast</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="op?.weather_forecast ?? ''"
            placeholder="Expected weather conditions…"
            :rows="4"
            @blur="saveOpField('weather_forecast', ($event.target as HTMLTextAreaElement).value)"
          />
        </div>
      </div>
    </div>

    <!-- OP Timing group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Operational Period Timing</h3>
      </div>
      <div class="p-3.5 grid grid-cols-2 gap-3">
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Start Date</label>
          <FosInput type="date" :model-value="op?.op_start_date ?? ''" @blur="saveOpField('op_start_date', ($event.target as HTMLInputElement).value)" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Start Time</label>
          <FosInput type="time" :model-value="op?.op_start_time ?? ''" @blur="saveOpField('op_start_time', ($event.target as HTMLInputElement).value)" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">End Date</label>
          <FosInput type="date" :model-value="op?.op_end_date ?? ''" @blur="saveOpField('op_end_date', ($event.target as HTMLInputElement).value)" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">End Time</label>
          <FosInput type="time" :model-value="op?.op_end_time ?? ''" @blur="saveOpField('op_end_time', ($event.target as HTMLInputElement).value)" />
        </div>
      </div>
    </div>

    <!-- Site Safety Plan group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Site Safety Plan</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <label class="inline-flex items-center gap-2 font-body text-[13px] text-on-surface cursor-pointer">
          <input
            id="site-safety-required"
            type="checkbox"
            :checked="op?.site_safety_plan_required ?? false"
            class="w-3.5 h-3.5 accent-primary cursor-pointer"
            @change="saveOpField('site_safety_plan_required', ($event.target as HTMLInputElement).checked)"
          />
          Site Safety Plan Required
        </label>
        <div v-if="op?.site_safety_plan_required">
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Location / Reference</label>
          <FosInput
            :model-value="op?.site_safety_plan_location ?? ''"
            placeholder="Where the site safety plan is located…"
            @blur="saveOpField('site_safety_plan_location', ($event.target as HTMLInputElement).value)"
          />
        </div>
      </div>
    </div>

    <!-- Prepared By -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
      </div>
      <div class="p-3.5">
        <FosInput
          :model-value="preparedBy202"
          placeholder="Name of person preparing ICS 202…"
          @blur="savePreparedBy(($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button
        class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors"
        @click="$emit('prev')"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Cover
      </button>
      <span class="flex-1" />
      <button
        class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity"
        @click="$emit('next')"
      >
        ICS 203
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>
  </div>
</template>
